import React from "react";
import { Box, Button, Divider, RadioGroup, FormControlLabel, Radio, Typography } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ReplayIcon from "@mui/icons-material/Replay";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import GetAppIcon from "@mui/icons-material/GetApp";
import { CompactSelect, CompactTextField } from "../../../Components/SharedComponents/SharedComponents";
import { ReconFilterFormData, SelectOption } from "./ReconCommon.types";


const BUY_SELL_OPTIONS: SelectOption[] = [
  { label: "All", value: "" },
  { label: "Buy", value: "buy" },
  { label: "Sell", value: "sell" },
];

const INPUT_SX = {
  "& .MuiInputBase-root": { fontSize: "0.7rem", height: "32px" },
  "& .MuiInputLabel-root": {
    fontSize: "0.7rem",
    transform: "translate(14px, 8px) scale(1)",
    "&.MuiInputLabel-shrink": { transform: "translate(14px, -6px) scale(0.75)" },
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#C0C0C0" },
  minWidth: 130,
  flex: "1 1 130px",
  maxWidth: 200,
} as const;

export type ViewType = "version-1" | "version-2";

interface Props {
  filters: ReconFilterFormData;
  updateFilter: (key: keyof ReconFilterFormData, value: string) => void;
  sourceOptions: SelectOption[];
  contractOptions: SelectOption[];
  viewType: ViewType;
  onViewTypeChange: (v: ViewType) => void;
  selectedFile: File | null;
  lastFileName?: string | null;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isRunning: boolean;
  hasRun: boolean;
  onRun: () => void;
  onClear: () => void;
  onDownloadTemplate: () => void;
  footerHint?: React.ReactNode;
}

export const ReconFilterActionsPanel: React.FC<Props> = ({
  filters, updateFilter, sourceOptions, contractOptions,
  viewType, onViewTypeChange,
  selectedFile, lastFileName, fileInputRef, onFileChange,
  isRunning, hasRun, onRun, onClear, onDownloadTemplate, footerHint,
}) => (
  <>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: "6px 12px", mb: 1, backgroundColor: "#fff", borderRadius: "4px", border: "1px solid #e0e0e0" }}>
      <Typography sx={{ fontSize: "0.62rem", color: "#777", fontWeight: 600 }}>View by:</Typography>
      <RadioGroup
        row
        value={viewType}
        onChange={(e) => onViewTypeChange(e.target.value as ViewType)}
        sx={{ display: "flex", gap: 0, "& .MuiRadio-root": { p: 0.4 }, "& .MuiFormControlLabel-root": { m: 0, mr: 1.5 } }}
      >
        <FormControlLabel value="version-1" control={<Radio size="small" sx={{ "&.Mui-checked": { color: "#1976d2" } }} />} label={<Box sx={{ fontSize: "0.72rem", fontWeight: 500 }}>Individual</Box>} />
        <FormControlLabel value="version-2" control={<Radio size="small" sx={{ "&.Mui-checked": { color: "#1976d2" } }} />} label={<Box sx={{ fontSize: "0.72rem", fontWeight: 500 }}>Aggregated</Box>} />
      </RadioGroup>
    </Box>

    <Box sx={{ p: 1.5, backgroundColor: "#fff", borderRadius: "4px", border: "1px solid #e0e0e0" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 1 }}>
        <CompactSelect label="Source" value={filters.source} onChange={(v) => updateFilter("source", v)} options={sourceOptions} name="source" slotProps={{ inputLabel: { shrink: true } }} sx={INPUT_SX} />
        {(["start_date", "end_date"] as const).map((key) => (
          <CompactTextField key={key} label={key === "start_date" ? "Trade Start Date" : "Trade End Date"} value={filters[key]} onChange={(v) => updateFilter(key, v)} name={key} type="date" slotProps={{ inputLabel: { shrink: true } }} sx={INPUT_SX} />
        ))}
        <CompactSelect label="Contract" value={filters.contract} onChange={(v) => updateFilter("contract", v)} options={contractOptions} name="contract" slotProps={{ inputLabel: { shrink: true } }} sx={INPUT_SX} />
        <CompactSelect label="Buy / Sell" value={filters.buy_sell} onChange={(v) => updateFilter("buy_sell", v)} options={BUY_SELL_OPTIONS} name="buy_sell" slotProps={{ inputLabel: { shrink: true } }} sx={INPUT_SX} />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.25 }} />

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
          <Typography sx={{ fontSize: "0.62rem", color: "#777" }}>
            BGM File{(selectedFile?.name ?? lastFileName) && ` — ${selectedFile?.name ?? lastFileName}`}
          </Typography>
          <Button variant="outlined" size="small" startIcon={<UploadFileIcon sx={{ fontSize: 14 }} />} onClick={() => fileInputRef.current?.click()}
            sx={{ height: 32, textTransform: "none", fontSize: "0.72rem", minWidth: 120, borderColor: selectedFile ? "#00bcd4" : "#C0C0C0", color: selectedFile ? "#00bcd4" : "inherit" }}>
            {selectedFile ? "Change File" : "Choose File"}
          </Button>
          <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12" style={{ display: "none" }} onChange={onFileChange} />
        </Box>

        <Button onClick={onDownloadTemplate} variant="outlined" size="small" startIcon={<GetAppIcon sx={{ fontSize: 14 }} />}
          sx={{ height: 32, textTransform: "none", fontSize: "0.72rem", minWidth: 120, color: "#777", borderColor: "#C0C0C0" }}>
          Download Template
        </Button>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.25 }} />

        <Button onClick={onRun} variant="contained" disabled={isRunning || !selectedFile}
          startIcon={!isRunning && (hasRun ? <ReplayIcon sx={{ fontSize: 15 }} /> : <PlayArrowIcon sx={{ fontSize: 15 }} />)}
          sx={{ height: 32, backgroundColor: "#00bcd4", textTransform: "none", fontSize: "0.75rem", minWidth: 150, "&:hover": { backgroundColor: "#008c9e" }, "&.Mui-disabled": { backgroundColor: "#e0e0e0" } }}>
          {isRunning ? "Running…" : hasRun ? "Re-run" : "Run Reconciliation"}
        </Button>

        <Button onClick={onClear} variant="outlined" color="inherit"
          sx={{ height: 32, textTransform: "none", fontSize: "0.75rem", minWidth: 80, color: "#777", borderColor: "#C0C0C0" }}>
          Clear
        </Button>
      </Box>

      {footerHint}
    </Box>
  </>
);